import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@mui/material';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import _ from 'lodash';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { closeConfirmationDialogAction, openConfirmationDialogAction } from 'store/global/actions';
import {
  assignFormFromTaskDraftAction,
  resetTaskFieldsDraftAction,
} from 'store/loanApplications/actions';
import { selectFormTaskDraftFields } from 'store/loanApplications/selectors';

import { TasksTestIds } from '../Tasks.testIds';

const useFormViewerClose = ({ formParams, setFormParams, setIsLoading }) => {
  const dispatch = useDispatch();
  const formTaskDraftFields = useSelector(selectFormTaskDraftFields);

  const onAssign = useCallback(() => {
    dispatch(closeConfirmationDialogAction());
    setIsLoading(true);
    dispatch(
      assignFormFromTaskDraftAction({
        formBundleId: formParams?.formId,
        draftFields: formTaskDraftFields,
      }),
    )
      .then(() => setFormParams(null))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, formParams, formTaskDraftFields, setFormParams, setIsLoading]);

  const onDiscard = useCallback(() => {
    dispatch(closeConfirmationDialogAction());
    dispatch(resetTaskFieldsDraftAction());
    setFormParams(null);
  }, [dispatch, setFormParams]);

  return useCallback(() => {
    if (_.isEmpty(formTaskDraftFields)) {
      dispatch(
        openConfirmationDialogAction({
          type: ConfirmationModalType.WARNING,
          message: 'Confirm close',
          description: 'Are you sure you want to close this dialog?',
          onAccept: () => setFormParams(null),
        }),
      );

      return;
    }

    // The task drafted in "Assign Task" is only created when the form is assigned, so closing here
    // would silently drop it. Both choices are explicit buttons so that dismissing the prompt
    // (backdrop click) returns to the editor with the draft intact instead of discarding it.
    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: 'Assign this form to the borrower?',
        description:
          'The form is saved, but the task has not been assigned yet. Assign it now so the borrower can complete it.',
        customButtons: (
          <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.CENTER} className="w-full">
            <Button
              className="flex-1"
              data-testid={TasksTestIds.UNASSIGNED_FORM_PROMPT_DISCARD_BUTTON}
              size="large"
              variant="line"
              onClick={onDiscard}
            >
              Close without assigning
            </Button>
            <Button
              className="flex-1"
              data-testid={TasksTestIds.UNASSIGNED_FORM_PROMPT_ASSIGN_BUTTON}
              size="large"
              onClick={onAssign}
            >
              Assign and close
            </Button>
          </ButtonGroupWrapper>
        ),
      }),
    );
  }, [dispatch, formTaskDraftFields, onAssign, onDiscard, setFormParams]);
};

export default useFormViewerClose;
